import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Theme,
} from "@mui/material";
import CustomInputButton from "../../components/Input/customInputButton";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { usePagination } from "../../hooks/usePagination";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { ContainedButton, TextButton } from "../../components/Button/AppButton";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { AssetTagResponse, IPagination } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import { AssetTagApi } from "../../services/assets/assetTag";
import { FilterModal } from "../FilterModal";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { alertDialogStore } from "../../store/dialogs";

interface InputProps {
  placeholder: string;
  label: string;
  handleChange: (items: TRowItem[]) => void;
  value?: TRowItem[];
  size?: "full" | "short" | "long";
  error?: boolean;
  errorMessage?: string;
}

interface Ifilter {
  name: string;
  asset_tag_field_type: string;
}

export const AssetTagSelectInput = ({
  placeholder,
  handleChange,
  label,
  error,
  value,
  size = "full",
  errorMessage = "required",
}: InputProps) => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [selectName, setSelectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedRowItems, setSelectedRowItems] = useState<TRowItem[]>([]);
  const [allAssetTags, setAllAssetTags] = useState<TRowItem[]>([]);
  const [querySet, setQuerySet] = useState({ is_deleted: false });

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(AssetTagApi.getAssetTags);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      name: "",
      asset_tag_field_type: "",
    });

  const populateDataSet = (
    data: AssetTagResponse[],
    pagination?: IPagination,
  ) => {
    setDataset(data);
    updatePaginationInitState(pagination);
  };

  const fetchAllAssetTags = async () => {
    try {
      const res = await AssetTagApi.getAssetTags({
        is_deleted: false,
        page_size: 1000,
      });
      if (res.data) {
        setAllAssetTags(res.data);
      }
    } catch (error) {
      alertDialogStore.open({
        title: "Failed to Load Asset Tags",
        subtitle: "Unable to fetch asset tags. Please try again.",
        isSuccess: false,
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await AssetTagApi.getAssetTags({
        is_deleted: false,
        page: 1,
        page_size: 30,
      });
      if (res.data) {
        populateDataSet(res.data, res.pagination);
      }

      await fetchAllAssetTags();

      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(selectedRowItems)) {
      setSelectedRowItems(value);
    }
  }, [value]);

  useEffect(() => {
    setSelectName(selectedRowItems.map((row) => row.name).join(", "));
  }, [selectedRowItems]);

  const [open, setOpen] = React.useState(false);

  const toggleModel = () => {
    if (!open && value && value.length > 0) {
      setSelectedRowItems(value);
    }
    setOpen(!open);
  };

  const handleClose = () => {
    handleChange(selectedRowItems);
    setOpen(false);
  };

  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      setQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    setQuerySet((p) => ({ ...p, ...data }));

    const filterRequest = async () => {
      setLoading(true);
      const res = await AssetTagApi.getAssetTags({
        is_deleted: false,
        ...data,
      });
      if (res.data) {
        populateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
  }, [form.name.value, form.asset_tag_field_type.value]);

  const toggleRow = (item: TRowItem) => {
    setSelectedRowItems((prevSelected) => {
      const isSelected = prevSelected.some(
        (selected) => selected.id === item.id,
      );
      const newSelected = isSelected
        ? prevSelected.filter((i) => i.id !== item.id)
        : [...prevSelected, item];

      handleChange(newSelected);
      return newSelected;
    });
  };

  const toggleSelectAllRow = () => {
    const currentPageIds = dataset.map((item) => item.id);
    const currentPageSelectedCount = selectedRowItems.filter((item) =>
      currentPageIds.includes(item.id),
    ).length;

    let newSelected: TRowItem[];

    if (currentPageSelectedCount === dataset.length) {
      if (selectedRowItems.length === allAssetTags.length) {
        newSelected = [];
      } else {
        newSelected = allAssetTags;
      }
    } else {
      newSelected = allAssetTags;
    }

    setSelectedRowItems(newSelected);
    handleChange(newSelected);
  };

  const handleNextPage = async () => {
    const response = await paginateToNextPage(querySet, populateDataSet)();
    return response;
  };

  const handlePreviousPage = async () => {
    const response = await paginateToPreviousPage(querySet, populateDataSet)();
    return response;
  };

  const handlePageNumber = async (page: number) => {
    const response = await paginateToPageNumber(
      populateDataSet,
      querySet,
    )(page);
    return response;
  };

  const getCurrentPageSelectedItems = () => {
    return selectedRowItems.filter((selected) =>
      dataset.some((current) => current.id === selected.id),
    );
  };

  const getSelectAllState = () => {
    const currentPageSelectedCount = getCurrentPageSelectedItems().length;
    const currentPageTotalCount = dataset.length;

    if (currentPageSelectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (
      currentPageSelectedCount === currentPageTotalCount &&
      selectedRowItems.length === allAssetTags.length
    ) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  return (
    <Box>
      <CustomInputButton
        loading={loading}
        label={label}
        placeholder={placeholder}
        value={selectName}
        handleChange={toggleModel}
        error={error}
        errorMessage={errorMessage}
        size={size}
      />
      <Dialog
        maxWidth={"xl"}
        open={open}
        sx={{ height: "100%" }}
        onClose={toggleModel}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          paddingRight={2}
        >
          <DialogTitle fontWeight={600}>{"Asset Tags"}</DialogTitle>
          {selectedRowItems && selectedRowItems.length > 0 ? (
            <DialogActions>
              <ContainedButton onClick={handleClose} title="Continue" />
            </DialogActions>
          ) : (
            <Box marginTop={2}>
              <TextButton title="Continue" />
            </Box>
          )}
        </Stack>

        <DialogContent sx={styles.content}>
          <Box sx={styles.filterBox}>
            <Stack direction={"row"} spacing={2}>
              <FilterModal
                label="Filters"
                loading={loading}
                closeOnoutSideClick
              >
                <Stack direction={"column"} spacing={2}>
                  <SideBarSearchInput
                    label="Asset Tag Name"
                    placeholder="Search by asset tag name ..."
                    value={form.name.value}
                    handleChange={updateInputField("name")}
                  />
                </Stack>
                <Box></Box>
              </FilterModal>

              {Object.entries(getFormValues())
                .filter(
                  ([_, value]) =>
                    typeof value === "string" && value.trim() !== "",
                )
                .map(([key, value]) => (
                  <FilterItemButton
                    key={key}
                    label={formatSnakeCaseToReadable(key)}
                    tip={value}
                    height={40}
                    onClick={() => resetFilter(key as keyof Ifilter)}
                  />
                ))}
            </Stack>
          </Box>

          <DamsTable
            isModel
            tableHeaders={columns}
            tableKeys={tkeys}
            data={dataset}
            hasPagination
            paginationState={pagination}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            setActivePage={handlePageNumber}
            useTableCheckbox
            onTableItemSelect={toggleRow}
            selectedRowItems={getCurrentPageSelectedItems()}
            toggleSelectAllRow={toggleSelectAllRow}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const styles = {
  filterBox: {
    width: "100%",
    marginBottom: 5,
  },
  content: (theme: Theme) => ({
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.up("xl")]: {
      width: "70vw",
    },
    width: "90vw",
  }),
  table: {
    width: "100vw",
    height: "70%",
  },
};

const tkeys = ["name", "asset_tag_field_type", "created_at"];
const columns = ["Asset Tag Name", "Asset Tag Field Type", "Date Added"];
